import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Loader2, UserPlus } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/auth'
import { authService } from '@/services/firebase.service'

const registerSchema = z.object({
  name: z.string().min(2, 'Name required'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Min 8 characters'),
  company: z.string().optional(),
})

type RegisterForm = z.infer<typeof registerSchema>

export function Register() {
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const { setUser } = useAuthStore()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  })

  const onSubmit = async (data: RegisterForm) => {
    setIsLoading(true)
    try {
      const firebaseUser = await authService.registerWithEmail(data.email, data.password, data.name)

      setUser({
        id: firebaseUser.uid,
        name: data.name,
        email: data.email,
        plan: 'free',
        minutesUsed: 0,
        minutesLimit: 60,
        voiceIdsUsed: 0,
        voiceIdsLimit: 3,
        createdAt: new Date().toISOString(),
      })

      toast.success('Account creato con successo!')
      navigate('/dashboard')
    } catch (error: any) {
      console.error('Register error:', error)
      if (error.code === 'auth/email-already-in-use') {
        toast.error('Email già registrata. Prova ad accedere.')
      } else if (error.code === 'auth/weak-password') {
        toast.error('Password troppo debole.')
      } else {
        toast.error('Errore registrazione: ' + (error.message || 'Riprova'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleRegister = async () => {
    setIsLoading(true)
    try {
      const firebaseUser = await authService.loginWithGoogle()
      const userProfile = await authService.getUserProfile(firebaseUser.uid)

      if (userProfile) {
        setUser(userProfile)
      } else {
        setUser({
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          name: firebaseUser.displayName || 'Utente',
          avatar: firebaseUser.photoURL || undefined,
          plan: 'free',
          minutesUsed: 0,
          minutesLimit: 60,
          voiceIdsUsed: 0,
          voiceIdsLimit: 3,
          createdAt: new Date().toISOString(),
        })
      }

      toast.success('Account creato con Google!')
      navigate('/dashboard')
    } catch (error: any) {
      toast.error('Errore: ' + (error.message || 'Riprova'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Create Workspace</h2>
        <p className="mt-2 text-sm text-slate-500">
          Start your 14-day Enterprise trial. No credit card required.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
            <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Full Name</label>
            <input
                {...register('name')}
                type="text"
                placeholder="John Doe"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50 focus:border-slate-400 transition-all"
            />
            {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
            </div>
            <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Company</label>
            <input
                {...register('company')}
                type="text"
                placeholder="Acme Inc."
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50 focus:border-slate-400 transition-all"
            />
            </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Work Email</label>
          <input
            {...register('email')}
            type="email"
            placeholder="name@company.com"
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50 focus:border-slate-400 transition-all"
          />
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Password</label>
          <div className="relative">
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              placeholder="Min 8 characters"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 pr-10 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50 focus:border-slate-400 transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-3 rounded-lg flex items-center justify-center gap-2 transition-all shadow-lg shadow-slate-900/20 active:scale-[0.98]"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Provisioning...
            </>
          ) : (
            <>
              <UserPlus className="h-4 w-4" />
              Create Secure Account
            </>
          )}
        </button>
      </form>

      <div className="mt-6">
        <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-slate-400">or continue with</span></div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handleGoogleRegister}
              disabled={isLoading}
              className="flex items-center justify-center gap-2 rounded-lg border border-slate-200 hover:bg-slate-50 py-2.5 text-sm font-medium text-slate-700 transition-colors disabled:opacity-50"
            >
                <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Google
            </button>
            <button type="button" disabled className="flex items-center justify-center gap-2 rounded-lg border border-slate-200 py-2.5 text-sm font-medium text-slate-400 cursor-not-allowed">
                 Microsoft
            </button>
        </div>
      </div>

      <p className="mt-8 text-center text-sm text-slate-500">
        Already have an account?{' '}
        <Link to="/login" className="text-slate-900 font-semibold hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  )
}
